//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar, { ToolbarField } from './PageToolbar';
// ServiceCalView.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowPathIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import { NavLink, useNavigate } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import ServiceCallDetailsModal from '../components/ServiceCallDetailsModal';
import ServiceCallFormModal from '../components/ServiceCallFormModal';
import CalendarHeader from '../components/CalendarHeader';
import DayViewTimelineService from '../components/DayViewTimelineService';
import AdditionalTech from '../mobile/AdditionalTech';
import LegendCP from '../components/LegendCP';

const ServiceCalView = () => {
  const [serviceCalls, setServiceCalls] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState([]);
  const [viewMode, setViewMode] = useState('day'); // 'week' or 'day'
  // const [selectedDay, setSelectedDay] = useState(null); // Used in day view
  const [selectedDay, setSelectedDay] = useState(new Date());
  const apiUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(false);
  const timeSlots = Array.from({ length: 24 }, (_, hour) => hour);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state for adding/editing service call
  const [formData, setFormData] = useState({
    TROUBLE: '',
    CUSTOMER: '',
    ADDRESS: '',
    CALLED_BY: '',
    PHONE: '',
    TECH: '',
    NOTES: '',
    TIME: '',
    end_time: '',
    SCHEDULE_DATE: '',
    customer_id: '',
    additional_tech: '',
    tentative: false,
    status_description: '',
  });
  const [isEditing, setIsEditing] = useState(false); // Flag to check if editing
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false); // Modal state for viewing service call details
  const [selectedServiceCall, setSelectedServiceCall] = useState(null); // Selected service call for detail modal
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedTechs, setSelectedTechs] = useState([]); // New state for multiple techs
  /* ---------- new: department filter ---------- */
  const [selectedDept, setSelectedDept] = useState('Service'); // Default to 'Service' department

  /* a memo-ised list of departments for the dropdown */
  const departmentOptions = React.useMemo(() => {
    const depts = new Set(employees.map(e => e.department).filter(Boolean));
    return ['All', ...depts];
  }, [employees]);  

  useEffect(() => {
    setLoading(true);
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        });
        const employeesData = Array.isArray(response.data) ? response.data : [];
        if (employeesData.length === 0) {
          toast.error('No employees found!', { position: 'top-center' });
        }
        setEmployees(employeesData);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error('Session expired. Please login again.', { position: 'top-center' });
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
            position: 'top-center',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [apiUrl, navigate]);

  useEffect(() => {
   if (viewMode === 'day' && selectedDay) {
     fetchServiceCallsForDay(selectedDay);
   } else {
    const currentWeekStart = getWeekStart();
    setSelectedWeek(getWeekDates(currentWeekStart)); // Set the initial week (current week)
    fetchServiceCalls(apiUrl, navigate);
   }
 }, [viewMode, selectedDay, apiUrl, navigate]);

  const fetchServiceCalls = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/service_crud.php?getList=1`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        });
        const calls = Array.isArray(response.data) ? response.data : [];
        if (calls.length === 0) {
          // toast.error('No service calls found!', { position: 'top-center' });
        } else {
          // Ensure 'end_time' is included in each service call
          setServiceCalls(
            calls.map((call) => ({
              ...call,
              end_time: call.end_time || '', // Default to empty string if not present
            }))
          );
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error('Session expired. Please login again.', {
            position: 'top-center',
          });
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
            position: 'top-center',
          });
        }
      } finally {
        setLoading(false);
      }
  };

  // Function to generate random colors for events
  const getRandomColor = () => {
    const colors = [
      'bg-red-200',
      'bg-blue-200',
      'bg-green-200',
      'bg-yellow-200',
      'bg-purple-200',
      'bg-pink-200',
      'bg-indigo-200',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  const techColors = {};
  const getTechColor = (techName) => {
    if (!techColors[techName]) {
      techColors[techName] = getRandomColor();
    }
    return techColors[techName];
  };

  const handleReset = () => {
    window.location.reload();
  };

  // Helper function to get the start of the week (Monday)
  const getWeekStart = (date = new Date()) => {
    const dayOfWeek = (date.getDay() + 6) % 7; // Adjust to get Monday as the first day
    const diff = date.getDate() - dayOfWeek;
    return new Date(date.setDate(diff));
  };

  // Get array of dates for the selected week
  const getWeekDates = (startOfWeek) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      dates.push(new Date(currentDay));
    }
    return dates;
  };

  // Handle moving to the previous week
  const handlePreviousWeek = () => {
    const previousWeekStart = new Date(selectedWeek[0]); // Start of the current week
    previousWeekStart.setDate(previousWeekStart.getDate() - 7); // Move back 7 days
    setSelectedWeek(getWeekDates(previousWeekStart));
  };

  // Handle moving to the next week
  const handleNextWeek = () => {
    const nextWeekStart = new Date(selectedWeek[0]); // Start of the current week
    nextWeekStart.setDate(nextWeekStart.getDate() + 7); // Move forward 7 days
    setSelectedWeek(getWeekDates(nextWeekStart));
  };

  // Handle moving to the previous day
  const handlePreviousDay = () => {
    const previousDay = new Date(selectedDay);
    previousDay.setDate(previousDay.getDate() - 1);
    setSelectedDay(previousDay);
    fetchServiceCallsForDay(previousDay); 
  };

  // Handle moving to the next day
  const handleNextDay = () => {
    const nextDay = new Date(selectedDay);
    nextDay.setDate(nextDay.getDate() + 1);
    setSelectedDay(nextDay);
    fetchServiceCallsForDay(nextDay); 
  };

  // Handle view mode change
  const handleViewChange = (e) => {
    const view = e.target.value;
    setViewMode(view);
    if (view === 'day') {
      setSelectedDay(prev => prev || new Date()); // only set if not already set
    }
  };

  // Handle opening the modal for adding/editing service call
  const openModal = (isEdit = false) => {
    setIsEditing(isEdit);
    setIsModalOpen(true);
  };

  // Handle closing the modal for adding/editing service call
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setFormData({
      TROUBLE: '',
      CUSTOMER: '',
      ADDRESS: '',
      CALLED_BY: '',
      PHONE: '',
      TECH: '',
      NOTES: '',
      TIME: '',
      end_time: '',
      SCHEDULE_DATE: '',
      customer_id: '',
      tentative: false,
      status_description: '',
    });
    setSelectedCompany(null);
  };

  const handleCompanySelect = (newJobSite) => {
    if (newJobSite && newJobSite.full_address) {
      setSelectedCompany(newJobSite);
      setFormData((prev) => ({
        ...prev,
        CUSTOMER: newJobSite.customer_name,
        ADDRESS: newJobSite.full_address,
        customer_id: newJobSite.customer_id,
      }));
    } else {
      // console.error('Invalid company selected:', newJobSite);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); 
    // Basic validation
    if (!formData.CUSTOMER || !formData.ADDRESS || !formData.TROUBLE) {
      toast.error('Please fill in the required fields.');
      setLoading(false);
      return;
    }
    // inside onSubmit before sending:
    if (formData.tentative && !formData.status_description?.trim()) {
      toast.error('Please provide a Status Description for tentative schedules.', { position: 'top-center' });
      setLoading(false);
      return;
    }
    
    try {
      if (isEditing) {
        const NewBodyData = {
          data: formData,
          id: formData.id,
          status: 'addServiceCall',
        };
        const response = await axios.post(`${apiUrl}/service_crud.php`, NewBodyData, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data[0] === 'success') {
          toast.success('Service call updated successfully!');
          closeModal();
          reFetchCurrentView();
        } else {
          toast.error(response.data.error || 'Failed to update service call.');
        }
      } else {
        const NewBodyData = {
          data: formData,
          id: 'new',
          status: 'addServiceCall',
        };
        // Add new service call
        const response = await axios.post(`${apiUrl}/service_crud.php`, NewBodyData, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data[0] === 'success') {
          toast.success('Service call added successfully!');
          closeModal();
          reFetchCurrentView();
        } else {
          toast.error(response.data.error || 'Failed to add service call.');
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking on a time slot
  const handleTimeSlotClick = (date, hour) => {
    // Build the datetime object
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour);
    selectedDateTime.setMinutes(0);
    selectedDateTime.setSeconds(0);
    selectedDateTime.setMilliseconds(0);

    // Calculate default end time (e.g., 1 hour later)
    const defaultEndTime = new Date(selectedDateTime.getTime() + 1 * 60 * 60 * 1000);

    // Open the modal
    openModal(false); // Not editing

    // Set the form data
    setFormData((prevData) => ({
      ...prevData,
      SCHEDULE_DATE: date.toLocaleDateString('en-CA'), // format as YYYY-MM-DD
      TIME: selectedDateTime.toTimeString().split(' ')[0].slice(0, 5), // format as HH:MM
      end_time: defaultEndTime.toTimeString().split(' ')[0].slice(0, 5),
    }));
  };

  // Handle clicking on a service call event
  const handleEventClick = (serviceCall) => {
    setSelectedServiceCall(serviceCall);
    setIsDetailModalOpen(true);
  };

  // Close the detail modal
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedServiceCall(null);
  };

  // Handle editing a service call
  const handleEditServiceCall = () => {
    // Populate form data with selected service call
    setFormData(selectedServiceCall);
    if(selectedServiceCall.add_tech && selectedServiceCall.add_tech.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        additional_tech: selectedServiceCall.add_tech[0].tech_id || '',
        tentative: selectedServiceCall.STATUS === 'tentative' ? true : false
      }));
    } else {
        setFormData((prevData) => ({
        ...prevData,
        tentative: selectedServiceCall.STATUS === 'tentative' ? true : false
      }));
    }
    
    closeDetailModal(); // Close the detail modal
    openModal(true); // Open the modal in edit mode
  };

  const handleDeptChange = async (e) => {
    const dept = e.target.value;
    setSelectedDept(dept);

    /* build the list of tech full-names that belong to that dept */
    const techNames =
      dept === 'All'
        ? []                                             // = show all
        : employees
            .filter(emp => emp.department === dept)
            .map(emp => `${emp.first_name} ${emp.last_name}`);

    /* trigger the same filtering you already have */
    const fakeEvent = { target: { options: techNames.map(t => ({ selected:true, value:t })) } };
    handleFilterTech(fakeEvent);
  };

  const handleFilterTech = async (event) => {
    const options = event.target.options;
    const selectedValues = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    setSelectedTechs(selectedValues);

    setLoading(true);
    try {
      let calls = [];
      if (selectedValues.length === 0) {
        // No techs selected, fetch all service calls
        const response = await axios.get(`${apiUrl}/service_crud.php?getList=1`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        });
        calls = Array.isArray(response.data) ? response.data : [];
      } else {
        const FilterBodyData = {
          techNames: selectedValues,
          status: 'filter_tech',
        };
        // Filter service calls by technicians
        const response = await axios.post(`${apiUrl}/service_crud.php`, FilterBodyData, {
          headers: { 'Content-Type': 'application/json' },
        });
        calls = Array.isArray(response.data) ? response.data : [];
      }

      if (calls.length === 0) {
        // toast.error('No service calls found!', { position: 'top-center' });
      } else {
        setServiceCalls(
          calls.map((call) => ({
            ...call,
            end_time: call.end_time || '',
          }))
        );
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.', {
          position: 'top-center',
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // fetch every call scheduled on one specific calendar date (YYYY-MM-DD)
  const fetchServiceCallsForDay = async (dateObj) => {
    setLoading(true);
    try {
      const iso = [
        dateObj.getFullYear(),
        (dateObj.getMonth() + 1).toString().padStart(2, '0'),
        dateObj.getDate().toString().padStart(2, '0')
      ].join('-');       // "2025-05-03"
      const { data } = await axios.get(`${apiUrl}/service_crud.php`, {
        params : { getListByDate: 1, date: iso }                // <-- backend needs this
      });
      const calls = Array.isArray(data) ? data : [];
      setServiceCalls(calls.map(c => ({ ...c, end_time: c.end_time || '' })));
    } catch (err) {
      toast.error('Could not load calls for that day');
    } finally {
      setLoading(false);
    }
  };

  const reFetchCurrentView = () => {
    if (viewMode === 'day' && selectedDay) {
      fetchServiceCallsForDay(selectedDay);
    } else {
      fetchServiceCalls();
    }
  }


  return (
    <div className="overflow-auto w-[80vw] mx-auto mt-8">
      <HeadingCp label={'Service Calendar View'} />
      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Service calendar tools" actions={<>
          <NavLink to="/full_scheduler_viewer" className="hidden sm:inline-flex">
            <button type="button" className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-400 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">Full Scheduler</button>
          </NavLink>
          <NavLink to={`/service_dashboard`}>
            <button type="button" className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-400 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Service Dashboard</button>
          </NavLink>
          <button type="button" onClick={() => openModal(false)} className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">New Service Call +</button>
        </>}>
          <ToolbarField label="Department"><select value={selectedDept} onChange={e => setSelectedDept(e.target.value)} className="mt-1 block w-40 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              <option value="">All Depts</option>
              {/* unique department list */}
              {[...new Set(employees.map(emp => emp.department))].map(dep => <option key={dep} value={dep}>{dep}</option>)}
            </select></ToolbarField>
          <CalendarHeader viewMode={viewMode} selectedWeek={selectedWeek} selectedDay={selectedDay} handlePreviousWeek={handlePreviousWeek} handleNextWeek={handleNextWeek} handlePreviousDay={handlePreviousDay} handleNextDay={handleNextDay} />
          <ToolbarField label="View"><select id="view-mode" className="rounded-md border border-gray-300 py-2 px-4" value={viewMode} onChange={handleViewChange}>
              <option value="week">Week View</option>
              <option value="day">Day View</option>
            </select></ToolbarField>
          <button onClick={handleReset} className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-md">
            <span className="flex items-center gap-1">
              <ArrowPathIcon className="h-5" />Reset</span>
          </button>
        </PageToolbar>
      </>

      <LegendCP color="fill-orange-300" label="Tentative" />
      <br />
      <br />
      {/* Calendar Grid */}
      <div className="overflow-auto">
      {/* <div className="overflow-auto w-[80vw] mx-auto">  80 % of viewport, centred */}
        <div className={`grid ${viewMode === 'week' ? 'grid-cols-8' : 'grid-cols-[auto_1fr]'}`}>
          {/* Week Columns */}
          
          {/* ---------- WEEK VIEW  ------------------------------------------------ */}
          {viewMode === 'week' && (
          <>
          {/* --------------- 1. frozen time column ---------------- */}
          <div className="col-span-1">
            {/* empty, sticky corner */}
            <div className="border border-gray-200 h-12 w-24 bg-gray-100 sticky top-0 z-20" />
            {/* hour labels */}
            {timeSlots.map(hour => (
              <div key={hour}
                className="border border-gray-200 h-16 text-right pr-2 pt-2 w-24 bg-white sticky left-0 z-10" >
                {`${hour % 12 || 12} ${hour >= 12 ? 'PM' : 'AM'}`}
              </div>
            ))}
          </div>

          {/* --------------- 2. one column per day ---------------- */}
          {selectedWeek.map((date, dayIdx) => (
            <div key={dayIdx} className="col-span-1 border-l relative">
              {/* day header (sticky) */}
              <div className="border border-gray-200 h-12 text-center bg-gray-100
                              sticky top-0 z-10">
                <span>{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                <br />
                <span>{date.getDate()}</span>
              </div>

              {/* timeline (24×1-hour rows == 64 px each) */}
              <div className="relative">
                {/* empty clickable rows */}
                {timeSlots.map(hour => (
                  <div
                    key={hour}
                    className="border border-gray-200 h-16 cursor-pointer hover:bg-gray-200"
                    onClick={() => handleTimeSlotClick(date, hour)}
                  />
                ))}

                {/* event blocks */}
                {serviceCalls
                  .filter(call => {
                    const d = new Date(`${call.SCHEDULE_DATE}T${call.TIME}`);
                    return d.toDateString() === date.toDateString();
                  })
                  .map((call, idx, arr) => {
                    /* ----- layout math ----- */
                    const start  = new Date(`${call.SCHEDULE_DATE}T${call.TIME}`);
                    const end    = call.end_time
                                  ? new Date(`${call.SCHEDULE_DATE}T${call.end_time}`)
                                  : new Date(start.getTime() + 60*60*1000);

                    const top    = (start.getHours() + start.getMinutes()/60) * 64;
                    const height = ((end - start) / 1000 / 60) / 60 * 64;

                    /* overlap algorithm (same as before) */
                    const overlaps = arr.filter(o=>{
                      const oS = new Date(`${o.SCHEDULE_DATE}T${o.TIME}`);
                      const oE = o.end_time
                                ? new Date(`${o.SCHEDULE_DATE}T${o.end_time}`)
                                : new Date(oS.getTime()+60*60*1000);
                      return oS < end && oE > start;
                    });
                    const index = overlaps.indexOf(call);
                    const width = 100 / overlaps.length;
                    const left  = index * width;

                    /* tech-filter */
                    if (selectedTechs.length && !selectedTechs.includes(call.TECH)) return null;
                    return (
                      <div
                        key={idx}
                        className={`absolute ${getTechColor(call.TECH)} p-2 rounded-lg
                                    text-xs leading-5 cursor-pointer hover:bg-opacity-75
                                    border border-gray-400`}
                        style={{ top, height, width:`${width}%`, left:`${left}%` }}
                        onClick={e => { e.stopPropagation(); handleEventClick(call); }}
                      >
                        {call.TECH
                          ? <p className="font-bold">{call.TECH}</p>
                          : <p className="font-bold text-red-700">Not assigned</p>}
                        
                          <p className="text-gray-500 truncate">{call.ADDRESS}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
          </>
          )}

          {/* Days Columns */}
          {viewMode === 'day' && (
            <DayViewTimelineService
              dayDate={selectedDay}
              calls={serviceCalls}
              employees={selectedDept ? employees.filter(e => e.department === selectedDept) : employees}
              selectedTechs={selectedTechs}
              onEmptySlot={(techId, hour, minute) => handleTimeSlotClick(selectedDay, hour)}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      </div>

      {/* Modal for Adding/Editing Service Call */}
      <ServiceCallFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        isEditing={isEditing}
        formData={formData}
        onInputChange={handleInputChange}
        onSubmit={handleFormSubmit}
        selectedCompany={selectedCompany} // from selection company component
        handleCompanySelect={handleCompanySelect} // from selection company component
        loading={loading}
        setFormData={setFormData}
      />

      {/* Modal for Viewing Service Call Details */}
      <ServiceCallDetailsModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        serviceCall={selectedServiceCall}
        onEdit={handleEditServiceCall}
      />
    </div>
  );
};

export default ServiceCalView;
